import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Infinity, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../mascot';
interface FreePracticeIntroProps {
  isOpen: boolean;
  onStart: () => void;
  onCancel: () => void;
}
export function FreePracticeIntro({
  isOpen,
  onStart,
  onCancel
}: FreePracticeIntroProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  return <AnimatePresence data-id="element-2766">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2767">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} data-id="element-2768" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-2769">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center relative" data-id="element-2770">
              <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" data-id="element-2771">
                <X className="w-5 h-5" data-id="element-2772" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md" data-id="element-2773">
                <Compass className="w-8 h-8 text-white" data-id="element-2774" />
              </div>
              <h2 className="text-2xl font-black tracking-tight" data-id="element-2775">
                Vrij Oefenen
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8" data-id="element-2776">
              <div className="flex gap-4 items-start mb-6" data-id="element-2777">
                <div className="flex-shrink-0 mt-1" data-id="element-2778">
                  <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-2779" />
                </div>
                <div className="bg-teal-50 rounded-2xl rounded-tl-none p-4 border border-teal-100 text-teal-900 text-sm font-medium leading-relaxed" data-id="element-2780">
                  Bij Vrij Oefenen kies je zelf wat je wilt doen. Geen regels,
                  geen tijdslimiet — jij bepaalt!
                </div>
              </div>

              <div className="space-y-4 mb-8" data-id="element-2781">
                <div className="flex items-start gap-3" data-id="element-2782">
                  <div className="mt-0.5 bg-teal-100 p-1 rounded-full text-teal-600" data-id="element-2783">
                    <Compass className="w-4 h-4" data-id="element-2784" />
                  </div>
                  <div data-id="element-2785">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2786">
                      Jouw keuze
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2787">
                      Kies elk onderwerp dat je wilt oefenen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2788">
                  <div className="mt-0.5 bg-emerald-100 p-1 rounded-full text-emerald-600" data-id="element-2789">
                    <Infinity className="w-4 h-4" data-id="element-2790" />
                  </div>
                  <div data-id="element-2791">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2792">
                      Geen limiet
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2793">
                      Oefen zo lang of kort als je wilt.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2794">
                  <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600" data-id="element-2795">
                    <Sparkles className="w-4 h-4" data-id="element-2796" />
                  </div>
                  <div data-id="element-2797">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2798">
                      Alle vakken
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2799">
                      Alles is beschikbaar — ook onderwerpen uit andere groepen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-2800">
                <Button variant="secondary" onClick={onCancel} className="flex-1" data-id="element-2801">
                  Niet nu
                </Button>
                <Button variant="primary" onClick={onStart} className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 border-none shadow-lg shadow-teal-200" data-id="element-2802">
                  Start!
                </Button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}